import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { prettyDate, formatHours } from "@/lib/api";

/**
 * Generate a weekly agenda PDF.
 *
 * @param {string} workshopName
 * @param {string} title - "Agenda General" or "Agenda Rápida"
 * @param {Array} days - [{date, is_holiday, holiday_label, is_workday, schedulable, used}]
 * @param {Object} agendaByDate - {date: [items...]}
 * @param {string} kind - "general" or "quick"
 */
export function generateAgendaPDF({ workshopName, title, days, agendaByDate, kind = "general" }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(workshopName || "Taller", 14, 18);
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120);
  doc.text(title, 14, 25);

  const range = days.length ? `${days[0].date} → ${days[days.length - 1].date}` : "";
  doc.setFontSize(10);
  doc.text(range, pageWidth - 14, 18, { align: "right" });

  const now = new Date();
  const printed = `Impreso: ${now.toLocaleDateString("es-ES")} ${now.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`;
  doc.text(printed, pageWidth - 14, 25, { align: "right" });

  doc.setTextColor(0);
  doc.setDrawColor(200);
  doc.line(14, 30, pageWidth - 14, 30);

  let y = 36;

  days.forEach((d) => {
    const items = agendaByDate[d.date] || [];
    const dayLabel = prettyDate(d.date);
    const capacityInfo = d.is_holiday
      ? `Festivo${d.holiday_label ? " · " + d.holiday_label : ""}`
      : !d.is_workday
        ? "No laborable"
        : `${formatHours(d.used)} / ${formatHours(d.schedulable)} programables`;

    // Day header
    if (y > 265) { doc.addPage(); y = 20; }
    doc.setFillColor(240, 245, 255);
    doc.rect(14, y - 5, pageWidth - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(dayLabel, 16, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(capacityInfo, pageWidth - 16, y, { align: "right" });
    doc.setTextColor(0);
    y += 5;

    if (items.length === 0) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(d.is_holiday ? "Día festivo" : "Sin citas", 18, y + 4);
      doc.setTextColor(0);
      y += 10;
      return;
    }

    // Items table
    const rows = items.map((it) => [
      it.license_plate || "-",
      `${it.brand || ""} ${it.model || ""}`.trim() || "-",
      it.description || "",
      it.customer_name || "",
      it.phone || "",
      formatHours(it.hours),
      (it.notes || "").slice(0, 60) + (((it.notes || "").length > 60) ? "…" : ""),
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Matrícula", "Vehículo", "Tarea", "Cliente", "Teléfono", "Horas", "Notas"]],
      body: rows,
      styles: { fontSize: 8, cellPadding: 1.5, valign: "top" },
      headStyles: {
        fillColor: kind === "quick" ? [251, 191, 36] : [37, 99, 235],
        textColor: 255,
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 22, fontStyle: "bold" },
        1: { cellWidth: 26 },
        2: { cellWidth: 40 },
        3: { cellWidth: 26 },
        4: { cellWidth: 22 },
        5: { cellWidth: 14, halign: "right", fontStyle: "bold" },
        6: { cellWidth: "auto" },
      },
      margin: { left: 14, right: 14 },
    });

    y = doc.lastAutoTable.finalY + 6;
  });

  // Footer with page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(`Página ${i} / ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 8, { align: "center" });
  }

  const fileName = `agenda-${kind === "quick" ? "rapida" : "general"}-${days[0]?.date || "sin-fecha"}.pdf`;
  doc.save(fileName);
}
